import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  Drawer, List, ListItem, ListItemIcon, ListItemText, Box, Collapse,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import { ACCESS, hasAccess } from "lib/constants/access-keys";

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: (props) => (props.open ? props.drawerWidth : 0),
    flexShrink: 0,
    whiteSpace: "nowrap",
    transition: "width 225ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
  drawerPaper: {
    width: (props) => (props.open ? props.drawerWidth : 0),
    backgroundColor: "#fff",
    borderRight: "none",
    boxShadow: (props) => (props.open ? "1px 0px 0px 0px rgba(0,0,0,0.15)" : "none"),
    display: "flex",
    flexDirection: "column",
    overflowX: "hidden",
    whiteSpace: "nowrap",
    transition: "width 225ms cubic-bezier(0.4, 0, 0.2, 1)",
  },
  topSpacer: {
    height: 56,
    flex: "none",
  },
  divider: {
    height: 1,
    backgroundColor: "#E4E7EC",
    margin: "0",
  },
  list: {
    paddingTop: 8,
    flex: 1,
  },
  listItem: {
    padding: "11px 20px",
    margin: 0,
    borderRadius: 0,
    borderLeft: "5px solid transparent",
    transition: "all 0.15s ease",
    cursor: "pointer",
    height: 46,
    "&.Mui-selected": {
      backgroundColor: "#EBEDEE",
      borderLeft: "5px solid #3A4D54",
      borderRadius: "0 4px 4px 0",
      "& $icon": { color: "#3A4D54" },
      "& $listText": { color: "#3A4D54" },
    },
    "&:hover": {
      backgroundColor: "#F9FAFB",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "#EBEDEE",
    },
  },
  icon: {
    color: "#3A4D54",
    minWidth: 43,
    "& .MuiSvgIcon-root": { fontSize: 24 },
  },
  listText: {
    color: "#3A4D54",
    "& .MuiListItemText-primary": {
      fontSize: 14,
      fontWeight: 500,
      fontFamily: "'Roboto', sans-serif",
      lineHeight: "20px",
    },
  },
  childItem: {
    padding: "9px 20px 9px 63px",
    height: 40,
  },
  childText: {
    color: "#3A4D54",
    "& .MuiListItemText-primary": {
      fontSize: 13.5,
      fontWeight: 500,
      fontFamily: "'Roboto', sans-serif",
      lineHeight: "20px",
    },
  },
  expandIcon: {
    color: "#98A2B3",
    fontSize: 20,
  },
}));

export const MENU_ITEMS = [
  {
    label: "Credit Transfer",
    icon: <SwapHorizIcon />,
    requires: ACCESS.CREDIT_TRANSFER,
    children: [
      { label: "New Transfer", path: "/portal/credit-transfer" },
      { label: "Transfer History", path: "/portal/transfer-history" },
      { label: "Notification Settings", path: "/portal/notification-settings" },
    ],
  },
];

export function getVisibleMenuItems(userAccess) {
  return MENU_ITEMS.filter((item) => !item.requires || hasAccess(userAccess, item.requires));
}

export default function Sidebar({ drawerWidth, open }) {
  const classes = useStyles({ drawerWidth, open });
  const history = useHistory();
  const location = useLocation();
  const userAccess = useSelector((state) => state.session.user?.access ?? []);
  const visibleItems = getVisibleMenuItems(userAccess);
  const [openMenus, setOpenMenus] = useState({});

  // Parents with children default to expanded.
  const isMenuOpen = (item) => (openMenus[item.label] !== undefined ? openMenus[item.label] : true);

  return (
    <Drawer className={classes.drawer} variant="permanent" classes={{ paper: classes.drawerPaper }}>
      <Box className={classes.topSpacer} />
      <Box className={classes.divider} />
      <List className={classes.list}>
        {visibleItems.map((item) => (item.children ? (
          <React.Fragment key={item.label}>
            <ListItem
              button
              className={classes.listItem}
              selected={!isMenuOpen(item) && item.children.some((child) => location.pathname.startsWith(child.path))}
              onClick={() => setOpenMenus((prev) => ({ ...prev, [item.label]: !isMenuOpen(item) }))}
            >
              <ListItemIcon className={classes.icon}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} className={classes.listText} />
              {isMenuOpen(item)
                ? <ExpandLess className={classes.expandIcon} />
                : <ExpandMore className={classes.expandIcon} />}
            </ListItem>
            <Collapse in={isMenuOpen(item)} timeout="auto" unmountOnExit>
              <List disablePadding>
                {item.children.map((child) => (
                  <ListItem
                    button
                    key={child.path}
                    className={`${classes.listItem} ${classes.childItem}`}
                    selected={location.pathname.startsWith(child.path)}
                    onClick={() => history.push(child.path)}
                  >
                    <ListItemText primary={child.label} className={classes.childText} />
                  </ListItem>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ) : (
          <ListItem
            button
            key={item.path}
            className={classes.listItem}
            selected={location.pathname.startsWith(item.path)}
            onClick={() => history.push(item.path)}
          >
            <ListItemIcon className={classes.icon}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} className={classes.listText} />
          </ListItem>
        )))}
      </List>
    </Drawer>
  );
}
